import {
  createContext,
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useContext,
  useEffect,
  useState
} from 'react';

import { useItemElementSubscription } from './hooks/useItemElementSubscription';
import { useRowCollapse } from './hooks/useRowCollapse';
import { TraceBoardCanEditItemFunction } from './interfaces/TraceBoardCanEditItemFunction';
import { TraceBoardCanLinkNewItemFunction } from './interfaces/TraceBoardCanLinkNewItemFunction';
import { TraceBoardColumn } from './interfaces/TraceBoardColumn';
import { TraceBoardFilterColumnItemsFunction } from './interfaces/TraceBoardFilterColumnItemsFunction';
import { TraceBoardFilterOrphanedItemsFunction } from './interfaces/TraceBoardFilterOrphanedItemsFunction';
import { TraceBoardItem } from './interfaces/TraceBoardItem';
import { TraceBoardOnAddItemFunction } from './interfaces/TraceBoardOnAddItem';
import { TraceBoardOnUpdateItemFunction } from './interfaces/TraceBoardOnUpdateItem';

interface SelectedItem {
  columnIndex: number;
  itemId: number | string;
}

export type TraceBoardContextState = ReturnType<typeof useRowCollapse> &
  ReturnType<typeof useItemElementSubscription> & {
    columns: TraceBoardColumn[];
    loadingItem?: TraceBoardItem;
    selected?: SelectedItem;
    canEditItem: TraceBoardCanEditItemFunction;
    canLinkNewItem: TraceBoardCanLinkNewItemFunction;
    setColumns: Dispatch<SetStateAction<TraceBoardColumn[]>>;
    setSelected: Dispatch<SetStateAction<SelectedItem | undefined>>;
    setLoadingItem: Dispatch<SetStateAction<TraceBoardItem | undefined>>;
    filterColumnItems: TraceBoardFilterColumnItemsFunction;
    filterOrphanedItems?: TraceBoardFilterOrphanedItemsFunction;
    onAddItem?: TraceBoardOnAddItemFunction;
    onUpdateItem?: TraceBoardOnUpdateItemFunction;
  };

export const TraceBoardContext = createContext<TraceBoardContextState>(null!);

export const useTraceBoardContext = () => {
  const context = useContext(TraceBoardContext);
  if (!context) {
    throw new Error(
      'useTraceBoardContext must be used within the TraceBoardProvider component'
    );
  }
  return context;
};

export interface TraceBoardProviderProps {
  innerRef?: MutableRefObject<TraceBoardContextState>;
  columns: TraceBoardColumn[];
  canEditItem?: boolean | TraceBoardCanEditItemFunction;
  canLinkNewItem?: boolean | TraceBoardCanLinkNewItemFunction;
  filterColumnItems: TraceBoardFilterColumnItemsFunction;
  filterOrphanedItems?: TraceBoardFilterOrphanedItemsFunction;
  onAddItem?: TraceBoardOnAddItemFunction;
  onUpdateItem?: TraceBoardOnUpdateItemFunction;
}

export const TraceBoardProvider: React.FC<TraceBoardProviderProps> = ({
  innerRef,
  children,
  canEditItem,
  canLinkNewItem,
  filterColumnItems,
  filterOrphanedItems,
  onAddItem,
  onUpdateItem,
  ...props
}) => {
  const {
    columnItemElements,
    registerItemElement,
    unregisterItemElement,
    subscribeItemElementChange,
    unsubscribeItemElementChange,
    publishItemElementChange
  } = useItemElementSubscription();
  const [columns, setColumns] = useState(props.columns);
  const { rowCollapse, toggleCollapseRow } = useRowCollapse(columns);
  const [selected, setSelected] = useState<SelectedItem>();
  const [loadingItem, setLoadingItem] = useState<TraceBoardItem>();

  // update columns state if changed from props
  useEffect(() => setColumns(props.columns), [props.columns]);

  // force convert to function
  const canEditItemFunc: TraceBoardCanEditItemFunction =
    typeof canEditItem === 'undefined'
      ? () => false
      : typeof canEditItem === 'boolean'
      ? () => canEditItem
      : canEditItem;
  const canLinkNewItemFunc: TraceBoardCanLinkNewItemFunction =
    typeof canLinkNewItem === 'undefined'
      ? () => false
      : typeof canLinkNewItem === 'boolean'
      ? () => canLinkNewItem
      : canLinkNewItem;
  const context = {
    columns,
    columnItemElements,
    rowCollapse,
    selected,
    loadingItem,
    canEditItem: canEditItemFunc,
    canLinkNewItem: canLinkNewItemFunc,
    setColumns,
    setSelected,
    setLoadingItem,
    registerItemElement,
    unregisterItemElement,
    subscribeItemElementChange,
    unsubscribeItemElementChange,
    publishItemElementChange,
    filterColumnItems,
    filterOrphanedItems,
    toggleCollapseRow,
    onAddItem,
    onUpdateItem
  };
  if (innerRef) {
    innerRef.current = context;
  }
  return (
    <TraceBoardContext.Provider value={context}>
      {children}
    </TraceBoardContext.Provider>
  );
};
